/**
 * Brand mailboxes: a dev-only debit ledger on top of the real MAIN wallet, kept out of the wallet
 * slice's own dependencies (the slice must not import a page folder).
 *
 * The balance itself is real: the mailbox purchase hook reads it from `state.ownerWallet`, so
 * funding the wallet shows up here and the card is forced only when the real balance is short.
 * What is not real yet is the charge: no backend can mint a mailbox invoice, so
 * `payInvoiceFromWallet` has nothing to settle and the real balance cannot move. Until it can,
 * each wallet settlement is recorded in a per-brand ledger (whole cents, persisted) under the
 * section's OWN localStorage key, and the owner-level total is subtracted on read inside
 * `resolveMainWalletForCurrency` (`store/slices/wallet.ts`), the one funnel every balance in the
 * app reads through, so the top bar, the Wallet page, the product sheets and the mailbox section
 * all show the same figure.
 *
 * WHY THIS FILE OWNS THE READ: the wallet slice must not depend on a page folder existing. A slice
 * import into `pages/<page>/store.ts` would make deleting the mock layer a refactor. So the key and
 * the summed read live HERE, in the marked mock-layer file, the section keeps writing its own key
 * from its own store, and the slice imports only this module.
 *
 * Inert in production; deleted with the rest of the mock layer once a real invoice settles and the
 * slice's own balance moves. The server's own balance is untouched until then.
 */

/** The ledger is active only outside production, the same gate the sections' feature flags use. */
export const WALLET_OVERRIDE_ACTIVE = process.env.NODE_ENV !== 'production';

/** The ledgers are USD spend (both scenarios price in USD); they only ever come off a USD balance. */
export const MAILBOX_LEDGER_CURRENCY = 'USD';

/**
 * The mailbox spend key(s). Each holds `Record<brandId, cents>` (whole cents, so e.g. `28.6` three
 * times is exactly `8580` on disk and never a floating-point tail). Owned and written by the
 * section's `store.ts`; read here in aggregate. Kept as a map (the aggregation reads its values)
 * so the read stays unchanged when the key is renamed.
 */
export const MAILBOX_LEDGER_KEYS = {
  // Value changed with the rename; the previous key is left orphaned, not reused, so a restore of
  // the pre-rename folder from its archive tag cannot fight this one over a shared key.
  mailbox: 'ows_mailbox_wallet_spent_v1',
} as const;

/** Every brand's spend under one key, summed in whole cents. Zero when the key is absent, malformed,
 *  or storage is blocked: the safe failure is showing the real balance in full. */
const readSpentCents = (key: string): number => {
  try {
    if (typeof window === 'undefined') return 0;
    const raw = window.localStorage.getItem(key);
    if (!raw) return 0;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return 0;
    let cents = 0;
    for (const value of Object.values(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value) && value >= 0) cents += Math.round(value);
    }
    return cents;
  } catch {
    return 0;
  }
};

/**
 * All local mailbox spend added together, in major units: the MAIN wallet is owner-level, so money
 * spent on any brand must come off the one balance the whole app shows. Summed in whole cents and
 * divided once, so three purchases are exactly 85.8.
 */
export const loadMailboxSpendTotal = (): number =>
  Object.values(MAILBOX_LEDGER_KEYS).reduce((cents, key) => cents + readSpentCents(key), 0) / 100;
